import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  Search, Plus, Pencil, Trash2, Loader2, Database, Globe, X,
} from "lucide-react";

/**
 * iter352.A — UsdaFoodsManager
 *
 * Live management panel for the 23,600+ USDA food collection
 * (`usda_foods`). Uses the endpoints introduced in iter351.H so
 * admins can add / edit / delete entries without a redeploy.
 *
 * Backend endpoints:
 *   GET    /api/admin/usda-foods/stats
 *   GET    /api/admin/usda-foods/search?q=<term>
 *   POST   /api/admin/usda-foods/upsert        (create / update)
 *   PUT    /api/admin/usda-foods/{id}/translate
 *   DELETE /api/admin/usda-foods/{id}
 *
 * Custom (non-USDA) records should use fdc_id ≥ 90,000,000 to avoid
 * collision with real USDA records.
 */

const EMPTY_FORM = {
  fdc_id: "",
  name_en: "",
  name_bg: "",
  category: "Custom",
  calories: "",
  protein: "",
  carbs: "",
  fat: "",
  fiber: "",
  sugar: "",
  saturated_fat: "",
};

const CUSTOM_BASE = 90000000;

export default function UsdaFoodsManager({ API, isDark }) {
  const [stats, setStats] = useState({ total: 0, categories: {} });
  const [query, setQuery] = useState("");
  const [foods, setFoods] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [translateTarget, setTranslateTarget] = useState(null);
  const [translateValue, setTranslateValue] = useState("");

  const fetchStats = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/admin/usda-foods/stats`);
      setStats({ total: data.total || 0, categories: data.categories || {} });
    } catch {
      toast.error("Грешка при зареждане на USDA статистика");
    }
  }, [API]);

  useEffect(() => { fetchStats(); }, [fetchStats]);

  const runSearch = async () => {
    if (!query || query.length < 2) {
      toast.warning("Въведете поне 2 символа");
      return;
    }
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/admin/usda-foods/search`, {
        params: { q: query, limit: 50 },
      });
      setFoods(data.foods || []);
      if ((data.foods || []).length === 0) toast.info("Няма намерени храни");
    } catch {
      toast.error("Грешка при търсене");
    } finally { setLoading(false); }
  };

  const openNew = () => {
    setEditing(null);
    setForm({ ...EMPTY_FORM, fdc_id: String(CUSTOM_BASE + Math.floor(Math.random() * 1000000)) });
    setShowForm(true);
  };

  const openEdit = (f) => {
    setEditing(f);
    setForm({
      fdc_id: String(f.fdc_id ?? ""),
      name_en: f.name_en || "",
      name_bg: f.name_bg || "",
      category: f.category || "Custom",
      calories: f.calories ?? "",
      protein: f.protein ?? "",
      carbs: f.carbs ?? "",
      fat: f.fat ?? "",
      fiber: f.fiber ?? "",
      sugar: f.sugar ?? "",
      saturated_fat: f.saturated_fat ?? "",
    });
    setShowForm(true);
  };

  const saveFood = async () => {
    if (!form.fdc_id || !form.name_en.trim()) {
      toast.error("fdc_id и name_en са задължителни");
      return;
    }
    const payload = {
      fdc_id: parseInt(form.fdc_id, 10),
      name_en: form.name_en.trim(),
      name_bg: form.name_bg.trim(),
      category: form.category || "Custom",
      calories: parseFloat(form.calories) || 0,
      protein: parseFloat(form.protein) || 0,
      carbs: parseFloat(form.carbs) || 0,
      fat: parseFloat(form.fat) || 0,
      fiber: parseFloat(form.fiber) || 0,
      sugar: parseFloat(form.sugar) || 0,
      saturated_fat: parseFloat(form.saturated_fat) || 0,
    };
    try {
      await axios.post(`${API}/admin/usda-foods/upsert`, payload);
      toast.success(editing ? "Храната е обновена" : "Нова храна е добавена");
      setShowForm(false);
      fetchStats();
      if (query) runSearch();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Грешка при запис");
    }
  };

  const deleteFood = async (fdc_id, name_en) => {
    if (!window.confirm(`Изтриване на "${name_en}" (fdc_id=${fdc_id})?`)) return;
    try {
      await axios.delete(`${API}/admin/usda-foods/${fdc_id}`);
      toast.success("Изтрита");
      setFoods((prev) => prev.filter((f) => f.fdc_id !== fdc_id));
      fetchStats();
    } catch {
      toast.error("Грешка при изтриване");
    }
  };

  const saveTranslation = async () => {
    if (!translateValue.trim()) { toast.warning("Въведете BG превод"); return; }
    try {
      await axios.put(`${API}/admin/usda-foods/${translateTarget.fdc_id}/translate`, {
        name_bg: translateValue.trim(),
      });
      toast.success("Преводът е записан");
      setFoods((prev) => prev.map((f) =>
        f.fdc_id === translateTarget.fdc_id ? { ...f, name_bg: translateValue.trim() } : f,
      ));
      setTranslateTarget(null);
      setTranslateValue("");
    } catch {
      toast.error("Грешка при запис на превода");
    }
  };

  const topCategories = Object.entries(stats.categories).slice(0, 6);

  return (
    <div className="space-y-4" data-testid="usda-foods-manager">
      {/* Stats Bar */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Card className={isDark ? "bg-gray-800 border-gray-700" : "card-elevated border-0"}>
          <CardContent className="p-4 flex items-center justify-between">
            <div>
              <p className={`text-xs ${isDark ? "text-gray-400" : "text-gray-500"}`}>USDA храни</p>
              <p className="text-2xl font-bold text-[#0EA5E9]" data-testid="usda-total-count">
                {stats.total.toLocaleString()}
              </p>
            </div>
            <Database className="w-8 h-8 text-[#0EA5E9]/30" />
          </CardContent>
        </Card>
        {topCategories.map(([cat, count]) => (
          <Card key={cat} className={isDark ? "bg-gray-800 border-gray-700" : "card-elevated border-0"}>
            <CardContent className="p-4">
              <p className={`text-[11px] truncate ${isDark ? "text-gray-400" : "text-gray-500"}`}>{cat || "Without category"}</p>
              <p className="text-xl font-bold" style={{ color: "#10B981" }}>{count.toLocaleString()}</p>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Toolbar */}
      <Card className={isDark ? "bg-gray-800 border-gray-700" : "card-elevated border-0"}>
        <CardContent className="p-4">
          <div className="flex flex-wrap items-center gap-3">
            <div className="relative flex-1 min-w-[220px]">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <Input
                placeholder="Търсене в USDA (мин. 2 символа)..."
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && runSearch()}
                className={`pl-10 ${isDark ? "bg-gray-700 border-gray-600" : "bg-[#F8FAFC]"}`}
                data-testid="usda-search-input"
              />
            </div>
            <Button size="sm" onClick={runSearch} disabled={loading} data-testid="usda-search-btn">
              {loading ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Search className="w-4 h-4 mr-1" />}
              Търси
            </Button>
            <Button
              size="sm"
              className="gradient-primary text-white"
              onClick={openNew}
              data-testid="usda-add-custom-btn"
            >
              <Plus className="w-4 h-4 mr-1" /> Нова персонализирана храна
            </Button>
          </div>
          <p className={`text-[11px] mt-2 ${isDark ? "text-gray-500" : "text-gray-400"}`}>
            Персонализираните храни автоматично получават fdc_id ≥ {CUSTOM_BASE.toLocaleString()}, за да не се припокриват с реални USDA записи.
          </p>
        </CardContent>
      </Card>

      {/* Results Table */}
      <Card className={isDark ? "bg-gray-800 border-gray-700" : "card-elevated border-0"}>
        <CardContent className="p-0">
          <ScrollArea className="h-[500px]">
            <table className="w-full text-sm" data-testid="usda-foods-table">
              <thead className={`sticky top-0 z-10 ${isDark ? "bg-gray-800" : "bg-gray-50"}`}>
                <tr className={`text-xs ${isDark ? "text-gray-400" : "text-gray-500"}`}>
                  <th className="text-left px-4 py-3 font-medium">fdc_id</th>
                  <th className="text-left px-2 py-3 font-medium">Name (EN)</th>
                  <th className="text-left px-2 py-3 font-medium">Име (BG)</th>
                  <th className="text-left px-2 py-3 font-medium">Категория</th>
                  <th className="text-right px-2 py-3 font-medium hidden md:table-cell">kcal</th>
                  <th className="text-right px-2 py-3 font-medium hidden md:table-cell">P</th>
                  <th className="text-right px-2 py-3 font-medium hidden md:table-cell">C</th>
                  <th className="text-right px-2 py-3 font-medium hidden md:table-cell">F</th>
                  <th className="text-right px-2 py-3 font-medium hidden lg:table-cell">Fib</th>
                  <th className="px-3 py-3 text-right font-medium">Действия</th>
                </tr>
              </thead>
              <tbody className={`divide-y ${isDark ? "divide-gray-700" : "divide-gray-100"}`}>
                {foods.map((f) => {
                  const isCustom = f.fdc_id >= CUSTOM_BASE;
                  return (
                    <tr
                      key={f.fdc_id}
                      className={`${isCustom ? (isDark ? "bg-indigo-900/10" : "bg-indigo-50/40") : ""} hover:${isDark ? "bg-gray-700/50" : "bg-gray-50"}`}
                      data-testid={`usda-row-${f.fdc_id}`}
                    >
                      <td className="px-4 py-2.5 font-mono text-xs">{f.fdc_id}{isCustom && <span className="ml-1 text-[10px] text-indigo-500">custom</span>}</td>
                      <td className={`px-2 py-2.5 ${isDark ? "text-white" : "text-[#1A1A2E]"} max-w-[260px] truncate`}>{f.name_en || "—"}</td>
                      <td className={`px-2 py-2.5 ${isDark ? "text-gray-300" : "text-gray-700"} max-w-[200px] truncate`}>
                        {f.name_bg || <span className="text-amber-500 text-xs">няма превод</span>}
                      </td>
                      <td className="px-2 py-2.5 text-xs text-gray-400">{f.category || "—"}</td>
                      <td className="px-2 py-2.5 text-right font-mono text-[11px] hidden md:table-cell text-gray-400">{f.calories ?? "—"}</td>
                      <td className="px-2 py-2.5 text-right font-mono text-[11px] hidden md:table-cell text-gray-400">{f.protein ?? "—"}</td>
                      <td className="px-2 py-2.5 text-right font-mono text-[11px] hidden md:table-cell text-gray-400">{f.carbs ?? "—"}</td>
                      <td className="px-2 py-2.5 text-right font-mono text-[11px] hidden md:table-cell text-gray-400">{f.fat ?? "—"}</td>
                      <td className="px-2 py-2.5 text-right font-mono text-[11px] hidden lg:table-cell text-gray-400">{f.fiber ?? "—"}</td>
                      <td className="px-3 py-2.5 text-right">
                        <div className="flex items-center justify-end gap-1">
                          <button
                            onClick={() => { setTranslateTarget(f); setTranslateValue(f.name_bg || ""); }}
                            className={`p-1.5 rounded-lg transition-colors ${isDark ? "hover:bg-gray-600" : "hover:bg-gray-100"}`}
                            title="Редактирай BG превод"
                            data-testid={`translate-${f.fdc_id}`}
                          >
                            <Globe className="w-3.5 h-3.5 text-[#10B981]" />
                          </button>
                          <button
                            onClick={() => openEdit(f)}
                            className={`p-1.5 rounded-lg transition-colors ${isDark ? "hover:bg-gray-600" : "hover:bg-gray-100"}`}
                            title="Редактирай"
                            data-testid={`edit-usda-${f.fdc_id}`}
                          >
                            <Pencil className="w-3.5 h-3.5 text-[#6366F1]" />
                          </button>
                          <button
                            onClick={() => deleteFood(f.fdc_id, f.name_en)}
                            className={`p-1.5 rounded-lg transition-colors ${isDark ? "hover:bg-gray-600" : "hover:bg-gray-100"}`}
                            title="Изтрий"
                            data-testid={`delete-usda-${f.fdc_id}`}
                          >
                            <Trash2 className="w-3.5 h-3.5 text-[#EF4444]" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
                {foods.length === 0 && (
                  <tr><td colSpan={10} className="text-center py-8 text-gray-400 text-sm">Стартирайте търсене или добавете нова храна.</td></tr>
                )}
              </tbody>
            </table>
          </ScrollArea>
        </CardContent>
      </Card>

      {/* Upsert Form Modal */}
      {showForm && (
        <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4" data-testid="usda-form-modal">
          <Card className={`w-full max-w-2xl ${isDark ? "bg-gray-800 border-gray-700" : "bg-white"}`}>
            <CardContent className="p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className={`text-lg font-bold ${isDark ? "text-white" : "text-[#1A1A2E]"}`}>
                  {editing ? `Редактиране — fdc_id ${form.fdc_id}` : "Нова персонализирана храна"}
                </h3>
                <button onClick={() => setShowForm(false)} data-testid="usda-form-close">
                  <X className="w-5 h-5 text-gray-400" />
                </button>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs text-gray-500">fdc_id *</label>
                  <Input
                    value={form.fdc_id}
                    onChange={(e) => setForm({ ...form, fdc_id: e.target.value })}
                    disabled={!!editing}
                    data-testid="usda-form-fdc-id"
                  />
                </div>
                <div>
                  <label className="text-xs text-gray-500">Категория</label>
                  <Input
                    value={form.category}
                    onChange={(e) => setForm({ ...form, category: e.target.value })}
                    data-testid="usda-form-category"
                  />
                </div>
                <div className="col-span-2">
                  <label className="text-xs text-gray-500">Name (EN) *</label>
                  <Input
                    value={form.name_en}
                    onChange={(e) => setForm({ ...form, name_en: e.target.value })}
                    data-testid="usda-form-name-en"
                  />
                </div>
                <div className="col-span-2">
                  <label className="text-xs text-gray-500">Име (BG)</label>
                  <Input
                    value={form.name_bg}
                    onChange={(e) => setForm({ ...form, name_bg: e.target.value })}
                    data-testid="usda-form-name-bg"
                  />
                </div>
                {[
                  ["calories", "kcal / 100g"],
                  ["protein", "Протеин (g)"],
                  ["carbs", "Въглехидрати (g)"],
                  ["fat", "Мазнини (g)"],
                  ["fiber", "Фибри (g)"],
                  ["sugar", "Захар (g)"],
                  ["saturated_fat", "Наситени мазнини (g)"],
                ].map(([key, label]) => (
                  <div key={key}>
                    <label className="text-xs text-gray-500">{label}</label>
                    <Input
                      type="number"
                      step="0.1"
                      value={form[key]}
                      onChange={(e) => setForm({ ...form, [key]: e.target.value })}
                      data-testid={`usda-form-${key}`}
                    />
                  </div>
                ))}
              </div>
              <div className="flex justify-end gap-2 mt-6">
                <Button variant="outline" onClick={() => setShowForm(false)} data-testid="usda-form-cancel">Отказ</Button>
                <Button className="gradient-primary text-white" onClick={saveFood} data-testid="usda-form-save">
                  {editing ? "Запазване на промените" : "Добавяне на храна"}
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Translate Modal */}
      {translateTarget && (
        <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4" data-testid="usda-translate-modal">
          <Card className={`w-full max-w-md ${isDark ? "bg-gray-800 border-gray-700" : "bg-white"}`}>
            <CardContent className="p-6">
              <div className="flex items-center justify-between mb-3">
                <h3 className={`text-lg font-bold ${isDark ? "text-white" : "text-[#1A1A2E]"}`}>BG превод</h3>
                <button onClick={() => setTranslateTarget(null)} data-testid="usda-translate-close">
                  <X className="w-5 h-5 text-gray-400" />
                </button>
              </div>
              <p className={`text-xs mb-2 ${isDark ? "text-gray-400" : "text-gray-500"}`}>{translateTarget.name_en}</p>
              <Input
                value={translateValue}
                onChange={(e) => setTranslateValue(e.target.value)}
                placeholder="Български превод..."
                data-testid="usda-translate-input"
              />
              <div className="flex justify-end gap-2 mt-4">
                <Button variant="outline" onClick={() => setTranslateTarget(null)}>Отказ</Button>
                <Button className="gradient-primary text-white" onClick={saveTranslation} data-testid="usda-translate-save">
                  Запази
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
}
